import { Alert, Box, Button, Paper, Typography } from '@mui/material'
import TooltipButton from '../../ui/components/tooltipButton'
import ReportsView from './View'
import PropTypes from 'prop-types'
import clsx from 'clsx'
import { useStyles } from './styles'
import useTranslations from '../../i18n/useTranslations'
import { useEffect, useState } from 'react'
import ReportsTableConfig from './TableReportConfig/View'
import ReportsTable from './TableReport/View'
import { useUserContext } from '../../auth/UserProvider'
import Loader from '../../ui/views/Loader'
import SearchInput from '../ShippingsTracking/SearchInput'
import {
  useDownloadReport,
  useGeneratedReportsTracking,
  useReportConfigTracking,
  useReportSubscriptions,
  useUpdateSubscriptionReport,
} from '../../services/reports'
import ModalLayout from '../../ui/modals/layout'
import ReportConfig from './ReportConfig'
import { format } from 'date-fns'
import { useHistory } from 'react-router-dom'
import * as routes from '../../routes'
import { ReactComponent as Boxes } from '../../assets/icons/boxes.svg'

const EmptyState = ({ setSeeConfig, emptySearch }) => {
  const classes = useStyles()
  const t = useTranslations()
  return (
    <Paper className={clsx(classes.paper, classes.emptyState)}>
      <Boxes />
      <Typography variant="h1" className={clsx('decorator')}>
        {t('reports:empty-reports')}
      </Typography>
      <Typography>
        <span className={classes.secondaryText}>
          {t('reports:empty-reports-description')}
        </span>
      </Typography>

      <Button onClick={() => setSeeConfig(true)} size={'medium'}>
        {t('reports:config')}
      </Button>
    </Paper>
  )
}

const Reports = () => {
  const t = useTranslations()
  const classes = useStyles()
  const user = useUserContext()
  const history = useHistory()
  useEffect(() => {
    if (!user.isMaster) {
      history.push(routes.HOME.linkTo())
    }
  }, [history, user])
  const [showAlert, setShowAlert] = useState()
  const [reportToConfig, setReportToConfig] = useState()
  const [isLoadingRefetch, setIsLoadingRefetch] = useState(false)
  const [seeConfig, setSeeConfig] = useState(false)
  const [tableParams, setTableParams] = useState({
    search: '',
    page: 0,
    rowsPerPage: 10,
    totalRows: 28,
    dateTo: format(new Date().setDate(new Date().getDate() + 1), 'MM-dd-yyyy'),
    dateFrom: format(
      new Date().setDate(new Date().getDate() - 7),
      'MM-dd-yyyy',
    ),
  })

  const { clientId } = useUserContext()

  const { mutateAsync: downloadReport, isLoading: isLoadingDownload } =
    useDownloadReport(clientId)

  const downloadOpenReport = async row => {
    try {
      const response = await downloadReport(row)

      const base64Data = response.data.data
      const csvText = atob(base64Data)

      const blob = new Blob([csvText], { type: 'text/csv' })

      const blobUrl = URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = blobUrl
      link.download = row.file_name
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
    } catch (error) {
      console.error('Error al descargar o abrir el reporte:', error)
    }
  }

  const {
    isLoading: isLoadingReportConfig,
    data: reportConfig,
    refetch: refetchReportConfig,
    setData: setReportConfig,
  } = useReportConfigTracking({
    filters: tableParams,
  })

  const {
    isLoading: isLoadingReportSubscriptions,
    data: reportSubscriptions,
    refetch: refetchReportSubscriptions,
    setData: setReportsSubscriptions,
  } = useReportSubscriptions({
    clientCode: clientId,
  })

  const {
    mutate: updateSub,
    isLoading: isLoadingUpdateSubs,
    isError: isErrorUpdateSub,
  } = useUpdateSubscriptionReport(clientId, refetchReportSubscriptions)

  const {
    isLoading: isLoadingGeneratedReportConfig,
    data: generatedReports,
    refetch: refetchGeneratedReports,
    setData: setGeneratedReports,
  } = useGeneratedReportsTracking({
    clientCode: clientId,
    filters: tableParams,
  })

  useEffect(() => {
    let timer
    if (showAlert !== null) {
      timer = setTimeout(() => {
        setShowAlert(null)
      }, 5000)
    }

    return () => clearTimeout(timer)
  }, [showAlert])

  useEffect(() => {
    const fetchData = async () => {
      await refetchReportConfig()
      await refetchGeneratedReports()
      await refetchReportSubscriptions()
    }

    fetchData()
  }, [
    tableParams.rowsPerPage,
    tableParams.page,
    tableParams.search,
    refetchReportConfig,
    refetchGeneratedReports,
    refetchReportSubscriptions,
    clientId,
  ])

  useEffect(() => {
    setTableParams({
      ...tableParams,
      totalRows: reportConfig?.pagination?.record_count,
    })
  }, [reportConfig])

  useEffect(() => {
    setTableParams({
      ...tableParams,
      totalRows: generatedReports?.pagination?.record_count,
    })
  }, [generatedReports])

  const renderConfigModal = () => {
    return (
      <ModalLayout
        onClose={() => {
          setReportToConfig(null)
        }}
        isOpen={reportToConfig}
        wide={200}
        content={
          <Box className={classes.modalContainer}>
            <ReportConfig />
          </Box>
        }
        title={t('reports:config-report')}
      />
    )
  }

  return (
    <>
      <ReportsView
        seeConfig={seeConfig}
        showAlert={showAlert}
        onClickConfig={() => setSeeConfig(!seeConfig)}
      >
        {renderConfigModal()}
        {/* {(reportConfig?.data?.length && seeConfig) ||
        (!seeConfig && generatedReports?.data?.length) ? (
          <SearchInput
            className={classes.searchInput}
            queryValue={tableParams.search}
            noHelperText={true}
            placeholder={t('home:shipment-recording-input-placeholder')}
            search={value => {
              setTableParams({ ...tableParams, search: value })
            }}
            clearSearch={() => {
              setTableParams({ ...tableParams, search: undefined })
            }}
          />
        ) : null} */}
        {isLoadingReportConfig ||
        isLoadingRefetch ||
        isLoadingGeneratedReportConfig ||
        isLoadingReportSubscriptions ? (
          <Loader />
        ) : generatedReports?.data?.length && !seeConfig ? (
          <ReportsTable
            rows={generatedReports?.data}
            downloadReport={downloadOpenReport}
            tableParams={tableParams}
            onChangeTableParams={setTableParams}
          />
        ) : seeConfig && reportConfig?.data?.length ? (
          <ReportsTableConfig
            rows={reportConfig?.data}
            reportSubscriptions={reportSubscriptions?.data}
            updateSub={updateSub}
            setReportToConfig={setReportToConfig}
            tableParams={tableParams}
            onChangeTableParams={setTableParams}
          />
        ) : (
          <EmptyState
            setSeeConfig={setSeeConfig}
            emptySearch={tableParams.search?.length}
          />
        )}
      </ReportsView>
    </>
  )
}

Reports.propTypes = {}
EmptyState.propTypes = {
  onClickConfig: PropTypes.func,
  emptySearch: PropTypes.bool,
}

export default Reports
